import { useState } from 'react';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Send } from 'lucide-react';
import { useClientRequest } from '@/hooks/useClientRequest';

const MAX_LENGTH = 800;

interface SuggestionBoxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  /** Prefix added to the message Phil receives, so he sees what kind of request it is. */
  prefix?: string;
}

export function SuggestionBoxDialog({
  open,
  onOpenChange,
  title = 'Send a suggestion',
  description = "Something you'd like to see in the app, or something that would help you practise? Tell Phil.",
  placeholder = 'For example: it would help me if I could...',
  prefix = 'Suggestion for the app:',
}: SuggestionBoxDialogProps) {
  const [text, setText] = useState('');
  const { sendRequest, sending } = useClientRequest();

  const handleSend = async () => {
    const ok = await sendRequest(`${prefix} ${text.trim()}`);
    if (ok) {
      setText('');
      onOpenChange(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => { if (!sending) onOpenChange(o); }}
      title={title}
      description={description}
    >
      <div className="space-y-3 px-1 pb-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LENGTH))}
          placeholder={placeholder}
          rows={5}
          autoFocus
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {text.length}/{MAX_LENGTH}
          </span>
          <Button onClick={handleSend} disabled={sending || !text.trim()} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send to Phil
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
